import { Link } from "react-router-dom";
import { ArrowRight, ChevronRight, MapPin } from "lucide-react";
import { useSEO, buildBreadcrumbsJsonLd } from "../lib/seo";
import { useSiteTexts } from "../lib/siteTexts";
import { pageFaqJsonLd, useFaqs } from "../lib/faqs";
import { SITE_URL } from "../lib/constants";
import { MEDIA_BY_KEY } from "../data/siteMedia";
import { PAGE_TEXTS } from "../data/pageTexts";
import { cityPath, useCities } from "../lib/werkgebied";
import { InfoHero } from "../components/info/InfoHero";
import { InfoSection } from "../components/info/InfoSections";
import { TextBody } from "../components/info/TextBody";
import { AdviceEndCta } from "../components/info/AdviceBlocks";
import { ProductSubnav } from "../components/product/ProductSubnav";
import { ProductFAQ } from "../components/product/ProductFAQ";
import { QuoteButton, WhatsAppButton } from "../components/home/CtaButtons";
import { CityImage } from "../components/werkgebied/CityHero";
import { ProductGrid } from "../components/werkgebied/CitySections";

const POINTS = ["Gevestigd in Maassluis", "Complete kozijnen inclusief glas en montage", "Advies per wijk en bouwperiode"];
const NAV = [["kernplaatsen", "Kernplaatsen"], ["plaatsen", "Alle plaatsen"], ["vestiging", "Vestiging"], ["aanbod", "Aanbod"]];

const PriorityCard = ({ c }) => (
  <Link to={cityPath(c.slug)} className="card card-hover group flex h-full flex-col overflow-hidden" data-testid={`werkgebied-priority-${c.slug}`}>
    <CityImage image={c.image} className="aspect-[16/10] rounded-none" sizes="(min-width: 1024px) 300px, calc(100vw - 32px)" />
    <div className="flex flex-1 flex-col p-5">
      <p className="text-[12px] font-semibold uppercase tracking-[0.14em] text-gold">{c.region}</p>
      <h3 className="mt-2 text-[19px] font-semibold text-ink group-hover:text-maas">Kozijnen {c.name}</h3>
      <p className="mt-2 line-clamp-3 flex-1 text-[14.5px] leading-relaxed text-slate-600">{c.lead}</p>
      <span className="mt-4 inline-flex items-center gap-1.5 text-[14px] font-semibold text-maas">Naar {c.name} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden="true" /></span>
    </div>
  </Link>
);

const PlaceLink = ({ c }) => (
  <Link to={cityPath(c.slug)} className="group flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3.5 transition-colors hover:border-maas" data-testid={`werkgebied-place-${c.slug}`}>
    <span><span className="flex items-center gap-2 text-[15px] font-semibold text-ink"><MapPin className="h-4 w-4 text-gold" aria-hidden="true" />{c.name}</span>
      {c.region && <span className="mt-0.5 block pl-6 text-[12.5px] text-slate-500">{c.region}</span>}</span>
    <ChevronRight className="h-4 w-4 shrink-0 text-slate-400 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
  </Link>
);

export default function Werkgebied() {
  const cfg = PAGE_TEXTS.werkgebied;
  const t = useSiteTexts("werkgebied");
  const [seo, hero, vestiging, missing] = ["seo", "hero", "vestiging", "missing"].map((k) => t(cfg.blocks[k]));
  const cities = useCities();
  const list = Array.isArray(cities) ? cities : [];
  const faqs = useFaqs(cfg.path);
  const crumbs = [{ label: "Home", href: "/" }, { label: "Werkgebied", href: cfg.path }];
  useSEO({ title: seo.title, description: seo.body, path: cfg.path, ogImage: SITE_URL + MEDIA_BY_KEY.ka_werkgebied_hero.src,
    faqJsonLd: pageFaqJsonLd(faqs, cfg.path), breadcrumbs: buildBreadcrumbsJsonLd(crumbs) });
  const priority = list.filter((c) => c.priority);
  const rest = list.filter((c) => !c.priority);

  return (
    <div data-testid="werkgebied-page">
      <InfoHero crumbs={crumbs} eyebrow="Werkgebied Zuid-Holland" block={hero} points={POINTS} mediaKey="ka_werkgebied_hero" location="werkgebied"
        badge={{ icon: MapPin, title: "Gevestigd in Maassluis", text: "Actief in Zuid-Holland" }} />
      <ProductSubnav items={NAV} />
      <InfoSection id="kernplaatsen" eyebrow="Kernplaatsen" title="Onze kernplaatsen" lead="Rond onze vestiging in Maassluis zijn we het vaakst en het snelst ter plaatse.">
        <ul className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4" data-testid="werkgebied-priority">
          {priority.map((c) => <li key={c.slug}><PriorityCard c={c} /></li>)}
        </ul>
      </InfoSection>
      {rest.length > 0 && (
        <InfoSection id="plaatsen" tone="slate" eyebrow="Plaatsen" title="Ook actief in deze plaatsen" lead="Kies uw plaats voor informatie over wijken, woningtypes en aandachtspunten.">
          <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="werkgebied-places">
            {rest.map((c) => <li key={c.slug}><PlaceLink c={c} /></li>)}
          </ul>
        </InfoSection>
      )}
      <InfoSection id="vestiging" eyebrow="Vestiging" title={vestiging.title}>
        <div className="grid gap-10 lg:grid-cols-12">
          <TextBody text={vestiging.body} className="lg:col-span-7" testid="werkgebied-vestiging" />
          <aside className="card h-fit p-6 lg:col-span-5" data-testid="werkgebied-missing">
            <h3 className="text-[18px] font-semibold text-ink">{missing.title}</h3>
            <p className="mt-2 text-[14.5px] leading-relaxed text-slate-600">{missing.body}</p>
            <div className="mt-5 flex flex-col items-start gap-3">
              <QuoteButton location="werkgebied-missing" className="w-full sm:w-auto" />
              <WhatsAppButton location="werkgebied-missing" />
            </div>
          </aside>
        </div>
      </InfoSection>
      <InfoSection id="aanbod" tone="slate" eyebrow="Aanbod" title="Wat wij in elke plaats leveren" lead="Altijd complete nieuwe kozijnen, puien en deuren, inclusief glas, montage en afwerking.">
        <ProductGrid location="werkgebied" />
      </InfoSection>
      <ProductFAQ faqs={faqs} title="Vragen over ons werkgebied" />
      <AdviceEndCta title="Nieuwe kozijnen voor uw woning in Zuid-Holland?" location="werkgebied" />
    </div>
  );
}
